import { useEffect, useState } from 'react';
import {
  Cradle,
  CreateMusicalReleaseProps,
  CreateRecordingProps,
  MusicalRelease,
  Recording,
  RecordingId,
  UpdateMusicalReleaseProps,
  UpdateRecordingProps,
  FileId,
  FileEntity,
} from '@solo/app-core';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, MusicalReleaseSummaryForm, RouterLink, Section, Text } from '@/app/ui';
import { translate } from '@/app/i18n';
import Colors from '@/app/styles/Colors';
import { FormProgress } from '@/app/ui/molecules';
import { MusicalReleaseDetailsForm, MusicalReleaseTracksForm } from '@/app/ui/organisms';
import { useCradle } from '@/app/contexts';
import { useBlocState } from '@/app/hooks';
// type MusicalReleaseFormParams = {
//   songType: string;
// };

enum Steps {
  INIT = 0,
  DETAILS = 1,
  TRACKS = 2,
  SUMMARY = 3,
}

const MusicalReleaseForm = () => {
  const navigate = useNavigate();
  // const { songType } = useParams<MusicalReleaseFormParams>();
  // const [type, setType] = useState<MusicalReleaseType | undefined>();
  const [currentStep, setCurrentStep] = useState<number>(Steps.DETAILS);
  const [musicalRelease, setMusicalRelease] = useState<MusicalRelease>();
  const addReleaseSteps = [
    translate('WORKS_PAGES.RELEASE_DETAILS'),
    translate('WORKS_PAGES.TRACKS'),
    // translate('WORKS_PAGES.DIGITAL_DISTRIBUTION'),
    translate('WORKS.SUMMARY'),
  ];
  const [showNavButtons, setShowNavButtons] = useState<boolean>(true);

  const { musicalWorksBloc, userBloc } = useCradle<Cradle>();
  const musicalWorksState = useBlocState(musicalWorksBloc);
  // const { digitalDistributionBloc } = useCradle<Cradle>();
  // const [digitalDistributions, setDigitalDistributions] = useState<Distribution[]>([]);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [artworkFile, setArworkFile] = useState<File | undefined>();
  const [audioFile, setAudioFile] = useState<File | undefined>();

  const [musicalReleaseDetails, setMusicalReleaseDetails] = useState<
    CreateMusicalReleaseProps | UpdateMusicalReleaseProps
  >();
  const [detailsFieldsMissing, setDetailsFieldsMissing] = useState<boolean>();

  // const [musicalReleaseDistribution, setMusicalReleaseDistribution] =
  //   useState<MusicalReleaseDistributionData>();

  async function getAudioInfo(audioId: FileId): Promise<FileEntity> {
    const audioInfo = await musicalWorksBloc.getAudioInfo(audioId);
    return audioInfo;
  }

  async function getMusicalRelease() {
    if (!musicalRelease) return;
    const musicalRel = await musicalWorksBloc.getMusicalRelease(musicalRelease.id);
    setMusicalRelease(musicalRel);
    setRecordings(musicalRel.recordings);
  }

  useEffect(() => {
    if (musicalRelease) getMusicalRelease();
  }, [musicalWorksState]);

  // useEffect(() => {
  //   let [firstStep] = addReleaseSteps;
  //   const [, ...restOfSteps] = addReleaseSteps;
  //   switch (type) {
  //     case 'EP':
  //       firstStep = translate('WORKS.EP_DETAILS');
  //       break;
  //     case 'SINGLE':
  //       firstStep = translate('WORKS.SINGLE_DETAILS');
  //       break;
  //     case 'ALBUM':
  //       firstStep = translate('WORKS.ALBUM_DETAILS');
  //       break;
  //     default:
  //   }
  //   setAddReleaseSteps([firstStep, ...restOfSteps]);
  // }, [type]);

  // useEffect(() => {
  //   if (!songType) return;
  //   setType(songType.toUpperCase() as MusicalReleaseType);
  //   setCurrentStep(1);
  // }, [songType]);

  function checkMandatoryDetailsFields() {
    if (!musicalReleaseDetails) return false;

    const hasArtwork =
      artworkFile !== undefined ||
      (musicalReleaseDetails.resourceId !== undefined && musicalReleaseDetails.resourceId !== '');

    return (
      musicalReleaseDetails.name !== undefined &&
      musicalReleaseDetails.name !== '' &&
      musicalReleaseDetails.languageId !== undefined &&
      musicalReleaseDetails.languageId !== 0 &&
      musicalReleaseDetails.primaryMusicStyleId !== undefined &&
      musicalReleaseDetails.primaryMusicStyleId !== 0 &&
      musicalReleaseDetails.artistName !== undefined &&
      musicalReleaseDetails.artistName !== '' &&
      musicalReleaseDetails.copyrightC !== undefined &&
      musicalReleaseDetails.copyrightC !== '' &&
      musicalReleaseDetails.copyrightP !== undefined &&
      musicalReleaseDetails.copyrightP !== '' &&
      hasArtwork
    );
  }

  const getUserDistributionData = async () => {
    const distributionUserData = await userBloc.getMeDistributionUserData();
    return distributionUserData;
  };

  async function createMusicalRelease() {
    if (!musicalReleaseDetails || !checkMandatoryDetailsFields()) {
      throw new Error('All mandatory fields must be filled!');
    }

    let musicalReleaseResponse: MusicalRelease | undefined;
    const userDistributionData = await getUserDistributionData();

    let resourceId: FileId = '';
    try {
      if (artworkFile) {
        resourceId = await musicalWorksBloc.uploadArtwork(artworkFile);
      }
    } catch (error) {
      toast.error(translate('TOAST.CHANGES_ERROR'));
    }

    if (musicalRelease) {
      try {
        await musicalWorksBloc.updateMusicalRelease(musicalRelease.id, {
          ...musicalReleaseDetails,
          artistAppleId: userDistributionData?.appleId,
          artistSpotifyId: userDistributionData?.spotifyId,
          resourceId: resourceId || musicalReleaseDetails.resourceId,
        });
      } catch (error) {
        toast.error(translate('TOAST.CHANGES_ERROR'));
      }

      musicalWorksBloc.loadMusicalReleases();
    } else {
      try {
        musicalReleaseResponse = await musicalWorksBloc.createMusicalRelease({
          ...musicalReleaseDetails,
          name: musicalReleaseDetails.name || '',
          artistName: musicalReleaseDetails.artistName || '',
          releasesLocals: musicalReleaseDetails.releasesLocals || [],
          artistLocals: musicalReleaseDetails.artistLocals || [],
          contributors: musicalReleaseDetails.contributors || [],
          distributions: musicalReleaseDetails.distributions || [],
          copyrightP: musicalReleaseDetails.copyrightP || '',
          copyrightC: musicalReleaseDetails.copyrightC || '',
          primaryMusicStyleId: musicalReleaseDetails.primaryMusicStyleId || 0,
          languageId: musicalReleaseDetails.languageId || 0,
          tracks: musicalReleaseDetails.tracks || [],
          resourceId: resourceId || 'resourceId',
          artistAppleId: userDistributionData?.appleId || '',
          artistSpotifyId: userDistributionData?.spotifyId || '',
        });
        setMusicalRelease(musicalReleaseResponse);
      } catch (error) {
        toast.error(translate('TOAST.CHANGES_ERROR'));
      }
    }
  }

  async function removeRecording(recordingId: RecordingId) {
    if (musicalRelease) {
      musicalWorksBloc.removeRecordingsFromMusicalRelease(musicalRelease.id, [recordingId]);
      toast.success(translate('TOAST.CHANGES_SUCCESS'));
    }
    getMusicalRelease();
  }

  async function createRecording(recording: CreateRecordingProps) {
    if (!musicalRelease) return;

    let resourceId: FileId = 'resourceId';
    if (audioFile) {
      try {
        resourceId = await musicalWorksBloc.uploadAudio(audioFile);
      } catch (error) {
        toast.error(translate('TOAST.CHANGES_ERROR'));
      }
    }

    try {
      await musicalWorksBloc.createRecording(
        { ...recording, audioFileId: resourceId },
        musicalRelease.id,
      );
      toast.success(translate('TOAST.CHANGES_SAVED'));
    } catch (error) {
      toast.error(translate('TOAST.SAVING_ERROR'));
    }

    getMusicalRelease();
  }

  // async function createRecording(recording: CreateRecordingProps) {
  //   let recordingResponse: Recording | undefined;
  //   if (!musicalRelease) return;
  //   try {
  //     recordingResponse = await musicalWorksBloc.createRecording(
  //       recording as CreateRecordingProps,
  //       musicalRelease.id,
  //     );
  //   } catch (error) {
  //     toast.error(translate('TOAST.CHANGES_ERROR'));
  //   }

  //   if (!recordingResponse || !audioFile) return;

  //   try {
  //     const buffer = Buffer.from(await audioFile.arrayBuffer());
  //     // Done in another branch
  //     // eslint-disable-next-line @typescript-eslint/no-unused-vars
  //     const audio: Audio = {
  //       file: buffer,
  //       name: audioFile.name,
  //       type: audioFile.type,
  //     };
  //     // await musicalWorksBloc.uploadAudio(audio);
  //   } catch (error) {
  //     toast.error(translate('TOAST.CHANGES_ERROR'));
  //   }

  //   getMusicalRelease();
  // }

  const reorderRecordings = async (recordingId: RecordingId, indexTo: number) => {
    if (!musicalRelease) return;

    await musicalWorksBloc.reorderReleaseRecordings(musicalRelease.id, recordingId, indexTo);

    getMusicalRelease();
  };

  async function updateRecording(recording: UpdateRecordingProps, recordingId: RecordingId) {
    musicalWorksBloc.updateRecording(recordingId, recording);

    getMusicalRelease();
  }

  function endMusicalReleaseForm() {
    setMusicalRelease(undefined);
    setRecordings([]);
    // setDigitalDistributions([]);
    setCurrentStep(0);
    toast.success(translate('WORKS.RELEASE_SUCCESS'));
  }

  async function handleNextStep(step: number) {
    switch (step) {
      case Steps.INIT:
        setMusicalRelease(undefined);
        break;
      case Steps.DETAILS:
        try {
          await createMusicalRelease();
          setCurrentStep(step + 1);
        } catch (error) {
          toast.error(translate('TOAST.CHANGES_ERROR'));
          setDetailsFieldsMissing(true);
        }
        break;
      case Steps.TRACKS:
        setCurrentStep(step + 1);
        break;
      case Steps.SUMMARY:
        try {
          // await saveDistributions();
          setCurrentStep(step + 1);
        } catch (error) {
          if (error instanceof Error) toast.error(error.message);
        }
        setCurrentStep(step + 1);
        break;
      default:
        break;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const goBack = () => {
    if (currentStep === Steps.DETAILS) {
      navigate('/works');
    }
    setCurrentStep(currentStep - 1);
  };

  return (
    <Section>
      {/* {type === undefined ? (
        <MusicalReleaseTypeForm />
      ) : ( */}
      <Flex.Container flexDirection="column" style={{ width: '100%' }} padding="0px 80px 40px 80px">
        <FormProgress
          steps={addReleaseSteps}
          currentStep={currentStep}
          canGoBack
          setCurrentStep={(step: number) => setCurrentStep(step)}
        />

        <Flex.Container
          flexDirection="column"
          gap="40px"
          padding="30px 80px 20px 80px"
          style={{ border: `1px solid ${Colors.borderInput}`, width: '100%' }}
        >
          {
            {
              1: (
                <MusicalReleaseDetailsForm
                  onChange={(details: Partial<CreateMusicalReleaseProps>) =>
                    setMusicalReleaseDetails(details)
                  }
                  musicalRelease={musicalRelease}
                  detailsFieldsMissing={detailsFieldsMissing}
                  setNewArtwork={(artwork: File) => setArworkFile(artwork)}
                />
              ),
              2: (
                <MusicalReleaseTracksForm
                  removeRecording={(recordingId) => removeRecording(recordingId)}
                  createRecording={(recording) => createRecording(recording)}
                  getAudioInfo={(audioId) => getAudioInfo(audioId)}
                  musicalRelease={musicalRelease}
                  updateRecording={(recording, recordingId) =>
                    updateRecording(recording, recordingId)
                  }
                  reorderRecordings={reorderRecordings}
                  recordings={recordings}
                  showNavButtons={(hide: boolean) => setShowNavButtons(hide)}
                  setNewAudio={(audio) => setAudioFile(audio)}
                />
              ),
              // 3: (
              //   <MusicalReleaseDistributionForm
              //     musicalid={id}
              //     distributionData={digitalDistributions}
              //     onChange={(distributionData) => setMusicalReleaseDistribution(distributionData)}
              //   />
              // ),
              3: (
                <MusicalReleaseSummaryForm
                  musicalRelease={musicalRelease as MusicalRelease}
                  // digitalDistributions={digitalDistributions}
                  recordings={recordings}
                  stepBackTo={(step) => setCurrentStep(step)}
                />
              ),
            }[currentStep]
          }
          {showNavButtons && (
            <Flex.Container style={{ marginLeft: 'auto' }} gap="20px">
              <Button $transparent $variant="primary" onClick={goBack}>
                <Text.SectionSubtitle>
                  {currentStep > 1
                    ? translate('WORKS_PAGES.BACK')
                    : translate('PROFILE_INFO.CANCEL')}
                </Text.SectionSubtitle>
              </Button>
              {currentStep < Steps.SUMMARY ? (
                <Button
                  $size="md"
                  $variant="primary"
                  style={{ width: '290px' }}
                  onClick={() => handleNextStep(currentStep)}
                >
                  <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
                    {translate('PAGINATION.NEXT_STEP')}
                  </Text.SectionSubtitle>
                </Button>
              ) : (
                <RouterLink
                  to="/works"
                  $size="md"
                  $variant="primary"
                  style={{ width: '290px' }}
                  onClick={() => {
                    endMusicalReleaseForm();
                  }}
                >
                  <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
                    {translate('PROFILE_INFO.SAVE')}
                  </Text.SectionSubtitle>
                </RouterLink>
              )}
            </Flex.Container>
          )}
        </Flex.Container>
      </Flex.Container>
    </Section>
  );
};

export default MusicalReleaseForm;
